import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useRef, useState } from "react";
import { Button, IconButton } from "@/components/ui/buttons";
import { Chip } from "@/components/ui/Chip";
import { Icon } from "@/components/ui/Icon";
import { getTranslations, type Locale } from "@/lib/i18n/config";
import { useBreakpoint } from "@/lib/hooks/useBreakpoint";

// TODO: Re-enable attachment feature once backend support is ready
const ATTACHMENT_ENABLED = false;
interface AgentFooterProps {
  onSendMessage?: (message: string, files?: File[]) => void;
  shouldAutoFocus?: boolean;
  locale?: Locale;
  /** True while a voice (speech-to-speech) session is running. */
  isVoiceActive?: boolean;
  /**
   * Mobile bottom-sheet state (MWR-609): blur the input on collapse — an open
   * keyboard would cover the very page the sheet is trying to reveal. Only
   * acted on below `lg`, where the sheet actually shrinks; the panel is a
   * side panel above that, so there is nothing to reveal and no keyboard to
   * dismiss.
   */
  isCollapsed?: boolean;
  /** Fired when the visitor focuses the input; expands the bottom sheet. */
  onInputFocus?: () => void;
  /** Omit to hide the voice control entirely. */
  onToggleVoice?: () => void;
  /**
   * True until the visitor has agreed to their input being processed. Replaces
   * the whole input row with the consent gate — there is deliberately nothing to
   * type into, because typing would be the thing we are not allowed to send yet.
   */
  consentRequired?: boolean;
  onGrantConsent?: () => void;
  onDeclineConsent?: () => void;
}
export const AgentFooter: React.FC<AgentFooterProps> = t0 => {
  const $ = _c(67);
  const {
    onSendMessage,
    shouldAutoFocus: t1,
    locale: t2,
    isVoiceActive: t3,
    isCollapsed: t4,
    onInputFocus,
    onToggleVoice,
    consentRequired: t5,
    onGrantConsent,
    onDeclineConsent
  } = t0;
  const shouldAutoFocus = t1 === undefined ? false : t1;
  const locale = t2 === undefined ? "de" : t2;
  const isVoiceActive = t3 === undefined ? false : t3;
  const isCollapsed = t4 === undefined ? false : t4;
  const consentRequired = t5 === undefined ? false : t5;
  const messageInputId = useId();
  const attachmentInputId = useId();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const [attachedFiles, setAttachedFiles] = useState(t6);
  const isDesktop = useBreakpoint("lg");
  let t7;
  if ($[1] !== locale) {
    t7 = getTranslations(locale);
    $[1] = locale;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const {
    agent: t
  } = t7;
  let t8;
  let t9;
  if ($[3] !== consentRequired || $[4] !== isVoiceActive || $[5] !== shouldAutoFocus) {
    t8 = () => {
      if (isVoiceActive || consentRequired) {
        textareaRef.current?.blur();
        return;
      }
      if (shouldAutoFocus) {
        const rafId = window.requestAnimationFrame(() => textareaRef.current?.focus());
        return () => {
          window.cancelAnimationFrame(rafId);
        };
      }
    };
    t9 = [consentRequired, isVoiceActive, shouldAutoFocus];
    $[3] = consentRequired;
    $[4] = isVoiceActive;
    $[5] = shouldAutoFocus;
    $[6] = t8;
    $[7] = t9;
  } else {
    t8 = $[6];
    t9 = $[7];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[8] !== isCollapsed || $[9] !== isDesktop) {
    t10 = () => {
      if (isCollapsed && !isDesktop) {
        textareaRef.current?.blur();
      }
    };
    t11 = [isCollapsed, isDesktop];
    $[8] = isCollapsed;
    $[9] = isDesktop;
    $[10] = t10;
    $[11] = t11;
  } else {
    t10 = $[10];
    t11 = $[11];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[12] !== consentRequired || $[13] !== isVoiceActive) {
    t12 = () => {
      const handleFocusRequest = () => {
        if (isVoiceActive || consentRequired) {
          return;
        }
        textareaRef.current?.focus();
      };
      window.addEventListener("agent:focus-input", handleFocusRequest);
      return () => {
        window.removeEventListener("agent:focus-input", handleFocusRequest);
      };
    };
    t13 = [consentRequired, isVoiceActive];
    $[12] = consentRequired;
    $[13] = isVoiceActive;
    $[14] = t12;
    $[15] = t13;
  } else {
    t12 = $[14];
    t13 = $[15];
  }
  useEffect(t12, t13);
  let t14;
  if ($[16] !== attachedFiles || $[17] !== onSendMessage) {
    t14 = () => {
      const message = textareaRef.current?.value ?? "";
      const hasMessage = message.trim().length > 0;
      if (!hasMessage && attachedFiles.length === 0) {
        return;
      }
      onSendMessage?.(message, attachedFiles.length > 0 ? attachedFiles : undefined);
      if (textareaRef.current) {
        textareaRef.current.value = "";
        textareaRef.current.style.height = "";
      }
      setAttachedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    $[16] = attachedFiles;
    $[17] = onSendMessage;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const handleSend = t14;
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }
      textarea.style.height = "0px";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 136)}px`;
    };
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const handleInput = t15;
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = event => {
      const files = Array.from(event.target.files ?? []);
      if (files.length > 0) {
        setAttachedFiles(prev => [...prev, ...files]);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const handleFileChange = t16;
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = index => {
      setAttachedFiles(prev_0 => prev_0.filter((_, i) => i !== index));
    };
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const handleRemoveAttachment = t17;
  if (consentRequired) {
    let t18;
    if ($[22] !== onGrantConsent || $[23] !== t.consent.accept) {
      t18 = <Button variant="primary" color="yellow" label={t.consent.accept} onClick={onGrantConsent} className="grow" />;
      $[22] = onGrantConsent;
      $[23] = t.consent.accept;
      $[24] = t18;
    } else {
      t18 = $[24];
    }
    let t19;
    if ($[25] !== onDeclineConsent || $[26] !== t.consent.decline) {
      t19 = <Button variant="secondary" color="yellow" label={t.consent.decline} onClick={onDeclineConsent} />;
      $[25] = onDeclineConsent;
      $[26] = t.consent.decline;
      $[27] = t19;
    } else {
      t19 = $[27];
    }
    let t20;
    if ($[28] !== t18 || $[29] !== t19) {
      t20 = <section className="flex items-center gap-2 px-4 pt-2 pb-safe-area">{t18}{t19}</section>;
      $[28] = t18;
      $[29] = t19;
      $[30] = t20;
    } else {
      t20 = $[30];
    }
    return t20;
  }
  let t18;
  if ($[31] !== attachedFiles || $[32] !== t.removeFile) {
    t18 = attachedFiles.length > 0 && <div className="mb-2 flex flex-wrap gap-1" aria-live="polite">{attachedFiles.map((file, index_0) => <Chip key={`${file.name}-${index_0}`} color="white" icon="close" iconAriaLabel={t.removeFile} onIconClick={() => handleRemoveAttachment(index_0)}>{file.name}</Chip>)}</div>;
    $[31] = attachedFiles;
    $[32] = t.removeFile;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== attachmentInputId) {
    t19 = ATTACHMENT_ENABLED && <input ref={fileInputRef} id={attachmentInputId} type="file" className="hidden" onChange={handleFileChange} accept=".pdf" multiple={true} />;
    $[34] = attachmentInputId;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== attachmentInputId || $[37] !== t.attachFile) {
    t20 = ATTACHMENT_ENABLED && <button type="button" onClick={() => fileInputRef.current?.click()} className="absolute left-3 bottom-3 z-10 text-white/70 cursor-pointer hover:text-yellow transition" aria-label={t.attachFile} aria-controls={attachmentInputId}><Icon name="attachment" size={24} decorative={true} /></button>;
    $[36] = attachmentInputId;
    $[37] = t.attachFile;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== handleSend) {
    t21 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    };
    $[39] = handleSend;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== messageInputId || $[42] !== onInputFocus || $[43] !== t.messageLabel || $[44] !== t.messagePlacholder || $[45] !== t21) {
    t22 = <textarea id={messageInputId} ref={textareaRef} onKeyDown={t21} onInput={handleInput} onFocus={onInputFocus} rows={1} className={`w-full border-box bg-black/40 backdrop-blur-xl ${ATTACHMENT_ENABLED ? "pl-11" : "pl-5"} pr-2 py-2 rounded-lg border border-white/50 text-white placeholder:text-white/70 focus:outline-2 focus:outline-yellow transition resize-none overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] align-middle mb-0.5`} placeholder={t.messagePlacholder} aria-label={t.messageLabel} />;
    $[41] = messageInputId;
    $[42] = onInputFocus;
    $[43] = t.messageLabel;
    $[44] = t.messagePlacholder;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t20 || $[48] !== t22) {
    t23 = <div className="relative w-full">{t20}{t22}</div>;
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t19 || $[51] !== t23) {
    t24 = <div className="flex items-end gap-2">{t19}{t23}</div>;
    $[50] = t19;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t18 || $[54] !== t24) {
    t25 = <div className="w-full">{t18}{t24}</div>;
    $[53] = t18;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== isVoiceActive || $[57] !== onToggleVoice || $[58] !== t.voice) {
    t26 = onToggleVoice && <IconButton icon={isVoiceActive ? "phone-off" : "microphone"} onClick={onToggleVoice} color={isVoiceActive ? "yellow" : "dark"} ariaLabel={isVoiceActive ? t.voice.stop : t.voice.start} />;
    $[56] = isVoiceActive;
    $[57] = onToggleVoice;
    $[58] = t.voice;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== handleSend || $[61] !== t.sendMessage) {
    t27 = <IconButton icon="paperplane" onClick={handleSend} color="dark" ariaLabel={t.sendMessage} />;
    $[60] = handleSend;
    $[61] = t.sendMessage;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t25 || $[64] !== t26 || $[65] !== t27) {
    t28 = <section className="flex items-end justify-between gap-2 px-4 pt-2 pb-safe-area">{t25}{t26}{t27}</section>;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  return t28;
};